import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'react-qr-code';
import { Link } from 'react-router-dom';
import { getSettings, getAllEntries, getOfficialWinners } from '../utils/db';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ConfettiEffect from '../components/ConfettiEffect';
import LiveWinnersMarquee from '../components/LiveWinnersMarquee';
import WinnerToasts from '../components/WinnerToasts';
import EngagementHighlights from '../components/EngagementHighlights';
import GlassPotTokenDraw from '../components/GlassPotTokenDraw';
import { playCountdownBeep } from '../utils/soundEffects';

const { 
  FiSettings, FiClock, FiSmartphone, FiAward, FiGift, FiTarget, 
  FiLayers, FiColumns, FiPauseCircle, FiShare2, FiLink, FiMessageCircle, 
  FiTwitter, FiFacebook, FiShuffle, FiX, FiRadio, FiTv, FiStar 
} = FiIcons;

const Home = () => {
  const [settings, setSettings] = useState(getSettings());
  const [entries, setEntries] = useState(getAllEntries());
  const [officialWinners, setOfficialWinners] = useState(getOfficialWinners());
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [showWinnersModal, setShowWinnersModal] = useState(false);
  const [showGlassPotModal, setShowGlassPotModal] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      setSettings(getSettings());
      setEntries(getAllEntries());
      setOfficialWinners(getOfficialWinners());
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('settingsUpdated', handleStorageChange);

    const pollInterval = setInterval(handleStorageChange, 4000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settingsUpdated', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, []);

  useEffect(() => {
    if (!settings.drawTime) return;

    const timer = setInterval(() => {
      const difference = +new Date(settings.drawTime) - +new Date();

      if (difference > 0) {
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        setTimeLeft({ hours, minutes, seconds });

        if (hours === 0 && minutes === 0 && seconds <= 10 && seconds > 0 && settings.soundEnabled) {
          playCountdownBeep(seconds === 1);
        }
      } else {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [settings.drawTime, settings.soundEnabled]);

  const baseUrl = window.location.origin + window.location.pathname;
  const isRandomMode = settings?.modeBehavior === 'random';
  const qrUrl = isRandomMode ? `${baseUrl}#/play` : `${baseUrl}#/${settings.activeGame || 'scratch'}`;
  
  const shareText = encodeURIComponent(`Join the ${settings.campaignName} and win exciting rewards! Play now:`);
  const shareUrl = encodeURIComponent(qrUrl);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrUrl);
    alert('Link copied to clipboard!');
  };

  const getGameStyling = () => {
    if (isRandomMode) {
      return { 
        name: 'Surprise Lucky Draw', 
        color: 'from-amber-500 via-pink-500 to-indigo-600', 
        bg: 'from-amber-500 via-pink-600 to-indigo-600', 
        icon: FiShuffle, 
        iconColor: 'text-amber-500' 
      };
    }
    switch(settings.activeGame) {
      case 'scratch': return { name: 'Scratch & Win', color: 'from-orange-500 to-pink-500', bg: 'from-orange-500 to-pink-600', icon: FiGift, iconColor: 'text-orange-500' };
      case 'spin': return { name: 'Spin the Wheel', color: 'from-blue-500 to-teal-500', bg: 'from-blue-500 to-teal-400', icon: FiTarget, iconColor: 'text-blue-500' };
      case 'peel': return { name: 'Peel & Reveal', color: 'from-indigo-500 to-purple-500', bg: 'from-indigo-500 to-purple-600', icon: FiLayers, iconColor: 'text-purple-500' };
      case 'slot': return { name: 'Slot Machine', color: 'from-red-500 to-amber-500', bg: 'from-red-500 to-red-700', icon: FiColumns, iconColor: 'text-red-500' };
      case 'flip': return { name: 'Mystery Flip Card', color: 'from-emerald-500 to-teal-500', bg: 'from-emerald-500 to-teal-600', icon: FiAward, iconColor: 'text-emerald-500' };
      default: return { name: 'Game', color: 'from-slate-500 to-slate-700', bg: 'from-slate-500 to-slate-600', icon: FiGift, iconColor: 'text-slate-500' };
    }
  };

  const style = getGameStyling();
  const isPaused = settings?.campaignStatus === 'paused';
  const isLivePotOnly = settings?.homeDisplayMode === 'live_pot_draw';
  const isDualMode = settings?.homeDisplayMode === 'both';

  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-fixed bg-no-repeat relative flex flex-col items-center justify-center p-4 sm:p-6 overflow-hidden bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 transition-colors duration-300"
      style={settings?.bgImage ? { backgroundImage: `url('${settings.bgImage}')` } : {}}
    >
      {/* Dynamic Overlay supporting both Light & Dark modes */}
      <div className="absolute inset-0 bg-slate-100/85 dark:bg-slate-950/90 backdrop-blur-md z-0 transition-colors duration-300"></div>

      {settings.resultsPublished && <ConfettiEffect />}

      {/* Brand Logo */}
      {settings.brandLogo && (
        <div className="absolute top-5 left-5 sm:top-6 sm:left-6 z-50 pointer-events-none">
          <img src={settings.brandLogo} alt="Brand Logo" className="h-10 md:h-14 object-contain drop-shadow-md" />
        </div>
      )}

      {/* Top Header Navigation Bar */}
      <div className="absolute top-5 right-5 sm:top-6 sm:right-6 z-50 flex flex-wrap items-center justify-end gap-2">
        {/* Toggle / View Live Token Pot Modal */}
        {!isLivePotOnly && (
          <button
            type="button"
            onClick={() => setShowGlassPotModal(true)}
            className="bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-800 dark:text-amber-200 px-3.5 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-black transition-all backdrop-blur-md shadow-sm cursor-pointer"
            title="Open Live Transparent Glass Pot Token Draw"
          >
            <span className="text-sm">🏺</span>
            <span>Live Token Pot</span>
          </button>
        )}

        {/* View Official Winners */}
        {officialWinners.length > 0 && !settings.resultsPublished && (
          <button
            type="button"
            onClick={() => setShowWinnersModal(true)}
            className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/40 text-amber-800 dark:text-amber-300 px-3.5 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-bold transition-all backdrop-blur-md shadow-sm cursor-pointer animate-pulse"
          >
            <SafeIcon icon={FiAward} className="text-amber-500 text-sm" />
            <span>🏆 View Winners ({officialWinners.length})</span>
          </button>
        )}

        {/* Admin Login Link */}
        <Link 
          to="/admin" 
          className="bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 border border-slate-200 dark:border-white/20 text-slate-700 dark:text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-bold transition-all backdrop-blur-md shadow-sm"
        >
          <SafeIcon icon={FiSettings} />
          <span>Admin</span>
        </Link>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col items-center mt-16 sm:mt-12 md:mt-4">
        
        {/* -------------------------------------------------------------------------- */}
        {/* MODE 1: LIVE POT DRAW CEREMONY (WHEN DRAWING OCCURS - HIDES QR CODE)       */}
        {/* -------------------------------------------------------------------------- */}
        {isLivePotOnly ? (
          <motion.div 
            key="live-pot-exclusive"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-5xl py-6 space-y-6"
          >
            {/* Live Ceremony Stage Header Banner */}
            <div className="p-4 rounded-2xl bg-amber-500/15 border-2 border-amber-500/40 text-center space-y-1 shadow-sm">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-sm">
                <SafeIcon icon={FiRadio} className="animate-pulse" />
                <span>Live Stage Ceremonial Token Pot Draw</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                {settings.campaignName} — Official Mega Draw
              </h1>
              <p className="text-xs text-slate-600 dark:text-slate-300 max-w-xl mx-auto">
                Participant token entries are rotating live inside the transparent glass pot. Watch the ceremonial reverse draw (3rd ➔ 2nd ➔ 1st) in real time!
              </p>
            </div>

            {/* THE GLASS POT TOKEN DRAW (PUBLIC SPECTATOR MODE - NO TRIGGER BUTTONS) */}
            <div className="rounded-3xl shadow-xl overflow-hidden">
              <GlassPotTokenDraw isAdmin={false} isStageMode={true} />
            </div>
          </motion.div>
        ) : (
          /* -------------------------------------------------------------------------- */
          /* MODE 2: VISITOR PARTICIPATION VIEW (NORMAL QR CODE & PARTICIPATION HERO)   */
          /* -------------------------------------------------------------------------- */
          <AnimatePresence mode="wait">
            {settings.resultsPublished ? (
              /* Published Results Display */
              <motion.div key="results" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-4xl text-center pb-20">
                <div className="mb-10">
                  <motion.div 
                    initial={{ y: -50, opacity: 0 }} 
                    animate={{ y: 0, opacity: 1 }}
                    className="inline-flex items-center justify-center p-6 bg-amber-500/20 text-amber-500 rounded-full mb-6 border border-amber-500/30 shadow-[0_0_50px_rgba(245,158,11,0.2)]"
                  >
                    <SafeIcon icon={FiAward} className="text-6xl" />
                  </motion.div>
                  <h1 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600 mb-4">
                    {settings.campaignName} Results
                  </h1>
                  <p className="text-lg text-slate-600 dark:text-slate-300">Congratulations to our official lucky winners!</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-center">
                  {officialWinners.length > 0 ? officialWinners.map((winner, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.2 }}
                      className={`glass-panel bg-white/95 dark:bg-slate-800/60 rounded-3xl p-8 relative overflow-hidden text-left border shadow-md ${
                        idx === 0 
                          ? 'border-amber-500/50 bg-amber-500/10 dark:bg-amber-900/20 md:col-span-2' 
                          : 'border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      {idx === 0 && (
                        <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-black text-xs px-6 py-2 rounded-bl-2xl shadow-md uppercase tracking-wider">
                          GRAND CHAMPION
                        </div>
                      )}
                      <div className="flex items-center gap-6">
                        <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-3xl font-black shadow-lg ${
                          idx === 0 
                            ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-slate-950' 
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                        }`}>
                          #{idx + 1}
                        </div>
                        <div>
                          <h3 className={`font-black uppercase tracking-wider ${idx === 0 ? 'text-2xl sm:text-3xl text-slate-900 dark:text-white' : 'text-xl sm:text-2xl text-slate-800 dark:text-slate-200'}`}>
                            {winner.name}
                          </h3>
                          <p className="text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2 text-xs sm:text-sm">
                            {winner.mobile}
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-bold">{winner.gameType || 'Participant'}</span>
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )) : (
                    <div className="col-span-full text-slate-500 py-10">No winners announced yet.</div>
                  )}
                </div>
              </motion.div>
            ) : (
              /* Normal Visitor Participation Hero */
              <motion.div key="display" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-14 pb-12">
                
                {/* Left Side: Campaign Info & Countdown Timer */}
                <div className="flex-1 text-center lg:text-left flex flex-col justify-center">
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-6">
                    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 dark:bg-white/5 border border-slate-200 dark:border-white/10 ${isPaused ? 'text-amber-600 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'} backdrop-blur-md shadow-sm text-xs font-bold`}>
                      <span className="relative flex h-2.5 w-2.5">
                        {isPaused ? (
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                        ) : (
                          <>
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                          </>
                        )}
                      </span>
                      {settings.campaignName} {isPaused ? 'Paused' : 'Live'}
                    </div>

                    {officialWinners.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowWinnersModal(true)}
                        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-800 dark:text-amber-300 text-xs font-bold backdrop-blur-sm cursor-pointer hover:bg-amber-500/25 transition-all shadow-sm"
                      >
                        <SafeIcon icon={FiAward} className="text-amber-500" />
                        <span>Winners Announced ({officialWinners.length})</span>
                      </button>
                    )}
                  </div>

                  <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-slate-900 dark:text-white mb-4 leading-tight tracking-tight">
                    Join the <br/>
                    <span className={`text-transparent bg-clip-text bg-gradient-to-r ${style.color}`}>
                      {style.name}
                    </span>
                  </h1>
                  
                  <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 mb-6 max-w-xl mx-auto lg:mx-0 leading-relaxed font-medium">
                    {isPaused ? 'The campaign has been temporarily paused by the organizer. Please stand by.' : settings.displaySubtitle}
                  </p>

                  {/* Countdown Timer Box */}
                  <div className="glass-panel p-5 sm:p-6 rounded-3xl inline-block bg-white/90 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 self-center lg:self-start shadow-md mb-6">
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800 pb-3 mb-4">
                      <SafeIcon icon={FiClock} />
                      <span className="font-bold uppercase tracking-wider text-xs">Time to Mega Draw</span>
                    </div>
                    
                    {settings.drawTime ? (
                      <div className="flex gap-4 sm:gap-6 text-center">
                        <div className="flex flex-col">
                          <span className="text-3xl sm:text-5xl font-mono font-black text-slate-900 dark:text-white">{String(timeLeft.hours).padStart(2, '0')}</span>
                          <span className="text-[10px] text-slate-400 uppercase mt-1 font-bold">Hours</span>
                        </div>
                        <span className="text-3xl sm:text-5xl font-mono font-black text-slate-300 dark:text-slate-600">:</span>
                        <div className="flex flex-col">
                          <span className="text-3xl sm:text-5xl font-mono font-black text-slate-900 dark:text-white">{String(timeLeft.minutes).padStart(2, '0')}</span>
                          <span className="text-[10px] text-slate-400 uppercase mt-1 font-bold">Minutes</span>
                        </div>
                        <span className="text-3xl sm:text-5xl font-mono font-black text-slate-300 dark:text-slate-600">:</span>
                        <div className="flex flex-col">
                          <span className={`text-3xl sm:text-5xl font-mono font-black ${timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds <= 10 ? 'text-red-500 animate-pulse' : 'text-slate-900 dark:text-white'}`}>
                            {String(timeLeft.seconds).padStart(2, '0')}
                          </span>
                          <span className="text-[10px] text-slate-400 uppercase mt-1 font-bold">Seconds</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm font-bold text-slate-500 py-2">
                        Draw time will be announced shortly
                      </div>
                    )}
                  </div>

                  {/* Dynamic Event Reward Pool */}
                  <EngagementHighlights 
                    entries={entries}
                    prizes={settings.prizes} 
                    gameMode={isRandomMode ? 'Surprise Mega Draw' : style.name} 
                  />
                </div>

                {/* Right Side: QR Code Card & Social Share Dock */}
                <div className="w-full max-w-sm sm:max-w-md flex flex-col items-center gap-5">
                  
                  {/* QR Card */}
                  <div className="w-full glass-panel bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl rounded-3xl p-6 sm:p-8 flex flex-col items-center">
                    <div className={`mb-5 flex items-center justify-center gap-2.5 font-black text-lg ${isPaused ? 'text-amber-500' : 'text-slate-800 dark:text-white'}`}>
                      <SafeIcon icon={isPaused ? FiPauseCircle : FiSmartphone} className="text-xl" />
                      <span>{isPaused ? 'Campaign Paused' : 'Scan with Phone to Play'}</span>
                    </div>
                    
                    <div className="bg-white p-3 border-4 border-slate-100 dark:border-slate-800 rounded-2xl shadow-inner relative overflow-hidden">
                      {isPaused && (
                        <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center rounded-xl pointer-events-none">
                          <span className="font-black tracking-widest text-slate-900 text-xl">PAUSED</span>
                        </div>
                      )}
                      <QRCode value={qrUrl} size={250} fgColor={isPaused ? "#cbd5e1" : "#0f172a"} level="H" />
                    </div>

                    <div className="mt-5 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 font-bold bg-slate-100 dark:bg-slate-800 px-4 py-2 rounded-full border border-slate-200 dark:border-slate-700">
                      <SafeIcon icon={style.icon} className={isPaused ? 'text-slate-400' : style.iconColor} />
                      <span>{style.name} Mode Active</span>
                    </div>
                  </div>

                  {/* Social Sharing Dock */}
                  <div className="glass-panel w-full bg-white/90 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 flex flex-col items-center shadow-sm">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                      <SafeIcon icon={FiShare2} />
                      <span>Share Campaign Link</span>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={copyToClipboard} className="w-9 h-9 rounded-full bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 flex items-center justify-center text-slate-700 dark:text-white transition-colors cursor-pointer" title="Copy Link">
                        <SafeIcon icon={FiLink} />
                      </button>
                      <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-[#25D366] hover:bg-[#1ebd5a] flex items-center justify-center text-white transition-colors" title="Share on WhatsApp">
                        <SafeIcon icon={FiMessageCircle} />
                      </a>
                      <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-[#1DA1F2] hover:bg-[#1a91da] flex items-center justify-center text-white transition-colors" title="Share on Twitter/X">
                        <SafeIcon icon={FiTwitter} />
                      </a>
                      <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-[#1877F2] hover:bg-[#166fe5] flex items-center justify-center text-white transition-colors" title="Share on Facebook">
                        <SafeIcon icon={FiFacebook} />
                      </a>
                    </div>
                  </div>

                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* DUAL MODE: Optional Glass Pot Showcase below participation (When explicitly set to 'both') */}
        {isDualMode && (
          <div className="w-full max-w-5xl mt-8 mb-16 relative z-20">
            <div className="p-3 mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-center text-xs font-bold text-amber-700 dark:text-amber-300">
              🏺 Live Token Glass Pot Showcase (Dual Mode Active)
            </div>
            <GlassPotTokenDraw isAdmin={false} isStageMode={false} />
          </div>
        )}
      </div>

      {/* Interactive Official Winners Modal */}
      <AnimatePresence>
        {showWinnersModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md"
            onClick={() => setShowWinnersModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="glass-panel bg-white dark:bg-slate-900 border border-amber-500/30 rounded-3xl p-6 sm:p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl relative"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-500 text-2xl">
                    <SafeIcon icon={FiAward} />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">🏆 Official Winners Hall</h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{settings.campaignName} — Announced Winners</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowWinnersModal(false)}
                  className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-300 flex items-center justify-center transition-colors cursor-pointer"
                  title="Close"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              {officialWinners.length > 0 ? (
                <div className="space-y-3">
                  {officialWinners.map((winner, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-2xl border flex items-center justify-between gap-4 transition-all ${
                        idx === 0
                          ? 'bg-amber-500/10 border-amber-500/40 shadow-sm'
                          : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700/60'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg ${
                          idx === 0
                            ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-slate-950 shadow-md'
                            : idx === 1
                            ? 'bg-slate-300 text-slate-900'
                            : idx === 2
                            ? 'bg-amber-700 text-amber-100'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                        }`}>
                          #{idx + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900 dark:text-white text-sm sm:text-base">{winner.name}</span>
                            {idx === 0 && (
                              <span className="text-[9px] bg-amber-400 text-slate-950 font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                                Grand Prize
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2">
                            <span>{winner.mobile}</span>
                            {winner.gameType && (
                              <>
                                <span className="w-1 h-1 rounded-full bg-slate-400"></span>
                                <span className="text-[11px] font-medium">{winner.gameType}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-[10px] text-slate-400 font-bold uppercase">Reward</div>
                        <div className="text-xs sm:text-sm font-black text-amber-600 dark:text-amber-400">{winner.reward}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <SafeIcon icon={FiAward} className="text-4xl text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                  <p className="font-bold text-sm">No official winners have been published yet.</p>
                  <p className="text-xs text-slate-400 mt-1">Winners drawn from the stage will appear right here!</p>
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 text-center">
                <button
                  type="button"
                  onClick={() => setShowWinnersModal(false)}
                  className="px-6 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs transition-all cursor-pointer shadow-sm"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-Screen Glass Pot Draw Modal (When Opened on Demand) */}
      <AnimatePresence>
        {showGlassPotModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6 bg-slate-950/80 backdrop-blur-md"
            onClick={() => setShowGlassPotModal(false)}
          >
            <div 
              className="w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 border border-amber-500/30 rounded-3xl p-4 sm:p-6 shadow-2xl relative"
              onClick={e => e.stopPropagation()}
            >
              <GlassPotTokenDraw isAdmin={false} isStageMode={true} onClose={() => setShowGlassPotModal(false)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!settings.resultsPublished && !isPaused && <WinnerToasts entries={entries} />}
      {!settings.resultsPublished && !isPaused && <LiveWinnersMarquee entries={entries} />}
    </div>
  );
};

export default Home;